import { useState, useEffect } from 'react';
import { Box, Alert } from '@mui/material';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import IconTextField from '../../../common/components/IconTextfield';
import Visibility from '../../../common/components/Visibility';
import SignService from '../services/signService';
import UnAuthorizedException from '../../../common/exeptions/UnAuthorizedException';
import TransactionService from '../services/transactionService';
import { useNavigate } from 'react-router-dom';
import { TransactionType } from '../../../application/types/transactionType';
import { AmbulanceTransactionForm } from '../../../application/types/form/ambulanceTransactionForm';
import { AxiosResponse } from 'axios';
import { Checkbox, IconButton, InputAdornment } from '@mui/material';
import { TransactionFormData } from '../../../application/types/form/transactionFormData';
import { DisposedFormData } from '../../../application/types/form/disposedFormData.ts';
import { ShiftChangeInventoryFormData } from '../../../application/types/form/shiftChangeInventoryForm.ts';
import { InventoryRecordsFormData } from '../../../application/types/form/inventoryRecordsForm.ts';
import { TransactionOverview } from '../../../application/types/transactionType';
import { useGlobalStore } from '../../../application/store/store';
import {
    DoubleSignatureData,
    InventoryDoubleSignatureData,
} from '../../../application/types/doubleSignatureData.ts';
import { ProfileService } from '../../profile/Services/ProfileService.ts';
import { NavigationRoutes } from '../../../application/router/routes.ts';
import CircularProgress from '@mui/material/CircularProgress';
import { isSignButtonDisabled } from '../../../common/utils/transactionValidation.tsx';
import { PatientData } from '../../../application/types/patientData';
import React from 'react';
import { StorageLocationType } from '../../../application/types/storageLocation.ts';
import { faLock, faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Helper function to check if patient data is empty
const isPatientDataEmpty = (patientData: PatientData): boolean => {
    const isEmpty = (value: string | null): boolean => {
        return value === null || value === undefined || value.trim() === '';
    };

    return (
        isEmpty(patientData.firstname) &&
        isEmpty(patientData.lastname) &&
        isEmpty(patientData.pnr)
    );
};

// Helper function to get patient identification or null if empty
const getPatientIdentification = (
    patientData: PatientData
): PatientData | null => {
    return isPatientDataEmpty(patientData) ? null : patientData;
};

export default function Sign({
    transactionType,
}: {
    transactionType: TransactionType;
}) {
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const [isProfileLoaded, setIsProfileLoaded] = useState<boolean>(false);
    const [isProfileLoading, setIsProfileLoading] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasSucceeded, setHasSucceeded] = useState<boolean>(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault();
    };
    const handleMouseUpPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault();
    };
    const [requestAmbulanceData, setRequestAmbulanceData] =
        useState<AmbulanceTransactionForm>({} as AmbulanceTransactionForm);
    const [
        requestShiftChangeInventoryFormData,
        setShiftChangeInventoryFormData,
    ] = useState<ShiftChangeInventoryFormData>(
        {} as ShiftChangeInventoryFormData
    );
    const [
        requestInventoryRecordsFormData,
        setRequestInventoryRecordsFormData,
    ] = useState<InventoryRecordsFormData>({} as InventoryRecordsFormData);

    const [requestTransactionData, setRequestTransactionData] =
        useState<TransactionFormData>({} as TransactionFormData);

    const [requestDisposedFormData, setRequestDisposedTransactionData] =
        useState<DisposedFormData>({} as DisposedFormData);

    const navigate = useNavigate();

    const {
        note,
        selectedDrug,

        matchedSynonymId,
        patientData,
        storageRemaining,
        selectedStorage,
        selectedEmergency,
        emergencyRemaining,
        shouldBeInventory,
        quantityChanged,
        isDoubleSignature,
        acceptSaldoMismatchCheckbox,
        connectedTransactionRecordId,
        connectedTransactionRecordIds,
        profile,
        referensData,
        editMode,
        inventoryRecords,
        disposedQuantity,
        disposedCause,
        setAcceptSaldoMismatchCheckbox,
        setConnectedTransactionRecordId,
        setConnectedTransactionRecordIds,
        setAmbulanceOverview,
        setWithdralOverview,
        setDepositOverview,
        setDisposedOverview,
        resetOverviews,
        resetTransactionFormData,
        setShiftChangeInventoryOverview,
        setInventoryRecordOverview,
        resetSelectedDrug,
        resetAvailableStorage,
        resetPatient,
        resetNote,
        resetInventoryRecords,
        resetInventoryList,
        setProfile,
        setMessage,
        resetDisposedCause,
        resetReferensData,
        resetBarCodeState,
        resetDisposedFormData,
        resetAmbulanceFormData,
        resetShiftChangeInventoryFormData,
        // Transaction error slice
        transactionErrorMessage,
        transactionErrorType,
        isTransactionErrorVisible,
        setTransactionError,
        clearTransactionError,
    } = useGlobalStore();
    const clearMessages = () => {
        clearTransactionError();
        setWarningMessage(null);
    };
    const signButtonDisabled = isSignButtonDisabled(
        transactionType,
        selectedDrug,
        selectedStorage,
        quantityChanged,
        storageRemaining,
        selectedEmergency,
        emergencyRemaining
    );

    const fetchProfile = async () => {
        const profileService = new ProfileService();
        try {
            const result = await profileService.getProfile();
            setProfile(result);
            setIsProfileLoaded(true);
            setIsProfileLoading(false);
        } catch (error) {
            setTransactionError(
                'Misslyckades hämta profil. Försök igen.',
                'error'
            );
            setIsProfileLoading(false);
        }
    };

    useEffect(() => {
        if (!profile) {
            fetchProfile().then(() => {});
        } else {
            setIsProfileLoaded(true);
            setIsProfileLoading(false);
        }
    }, []);

    const populateData = (signatureObject) => {
        const { drugQuantities } = useGlobalStore.getState().drugQuantitiesView;
        const drugsInStorage = useGlobalStore.getState().drugsInStorage;
        const syncedDrugsInStorage = drugsInStorage.map((drug) => {
            const match = drugQuantities.find((q) => q.drugId === drug.drug.id);
            return {
                ...drug,
                quantityRemaining: match
                    ? match.quantityAccordingToUser
                    : drug.quantityRemaining,
            };
        });
        if (transactionType === TransactionType.AmbulanceTransaction) {
            const data: AmbulanceTransactionForm = {
                ...requestAmbulanceData,
                acceptSaldoMismatch: acceptSaldoMismatchCheckbox,
                drugId: selectedDrug.id,
                drug: selectedDrug,
                patientIdentification: getPatientIdentification(patientData),
                description: note,
                signature: signatureObject,
                changedQuantity: parseFloat(quantityChanged),
                withdrawRecord: {
                    remainingQuantityAccordingToUser:
                        parseFloat(storageRemaining),
                    storageLocationId: selectedStorage.id,
                    givenQuantity: null,
                    disposedQuantity: null,
                },
                depositRecord: {
                    remainingQuantityAccordingToUser:
                        parseFloat(emergencyRemaining),
                    storageLocationId: selectedEmergency.id,
                    givenQuantity: null,
                    disposedQuantity: null,
                },
            };
            setRequestAmbulanceData(data);
            return data;
        } else if (transactionType === TransactionType.ShiftCrosscheck) {
            const data: ShiftChangeInventoryFormData = {
                ...requestShiftChangeInventoryFormData,
                signature: {
                    userId: signatureObject.userId,
                    firstname: signatureObject.firstname,
                    lastname: signatureObject.lastname,
                    username: signatureObject.username,
                },
                doubleSignature: null,
                storageLocation: {
                    barCode: selectedStorage?.barCode,
                    id: selectedEmergency.id,
                    digitalId: selectedEmergency.digitalId || '',
                    name: selectedEmergency.name || '',
                    location: selectedEmergency.location || '',
                    description: selectedEmergency.description || '',
                    isSatelliteStorage:
                        selectedEmergency.isSatelliteStorage || false,
                    isDeleted: selectedEmergency.isDeleted || false,
                    type:
                        selectedEmergency?.type ??
                        StorageLocationType.AmbulanceBag,
                },
                crossCheckResults: syncedDrugsInStorage,
                shouldBeInventory: shouldBeInventory,
                acceptSaldoMismatch: acceptSaldoMismatchCheckbox,
            };
            setShiftChangeInventoryFormData(data);
            return data;
        } else if (transactionType === TransactionType.Crosscheck) {
            const data: InventoryRecordsFormData = {
                ...requestInventoryRecordsFormData,
                inventoryRecords: inventoryRecords.map((record) => ({
                    ...record,
                    description: note ?? null,
                })),
                storageLocationId: selectedStorage.id,
                signature: signatureObject,
                acceptSaldoMismatch: acceptSaldoMismatchCheckbox,
            };
            setRequestInventoryRecordsFormData(data);
            return data;
        } else if (transactionType === TransactionType.Disposed) {
            const data: DisposedFormData = {
                ...requestDisposedFormData,
                transactionType: transactionType,
                drug: selectedDrug,
                storageLocation: selectedStorage,
                changedQuantity: Number(disposedQuantity),
                remainingQuantityAccordingToUser: parseFloat(storageRemaining),
                givenQuantity: null,
                disposedQuantity: Number(disposedQuantity),
                administratedDateTime: new Date(),
                description: disposedCause.reasons.join(', '),
                medicalRecordId: referensData.journalId,
                patientIdentification: null,
                signature: signatureObject.userId,
                doubleSignature: null,
                acceptSaldoMismatch: acceptSaldoMismatchCheckbox,
            };
            setRequestDisposedTransactionData(data);
            return data;
        } else {
            const data: TransactionFormData = {
                ...requestTransactionData,
                transactionType: transactionType,
                drug: selectedDrug,
                storageLocation: selectedStorage,
                changedQuantity: parseFloat(quantityChanged),
                remainingQuantityAccordingToUser: Number(storageRemaining),
                givenQuantity: null,
                disposedQuantity: null,
                administratedDateTime: new Date(),
                description: note,
                medicalRecordId: referensData.journalId,
                patientIdentification: getPatientIdentification(patientData),
                signature: signatureObject.userId,
                doubleSignature: null,
                acceptSaldoMismatch: acceptSaldoMismatchCheckbox,
                selectedSynonymId: matchedSynonymId,
            };
            setRequestTransactionData(data);
            return data;
        }
    };

    const signTransaction = async (
        emailOrUserName: string,
        password: string
    ) => {
        try {
            return await SignService.signWithCredentials({
                emailOrUserName,
                password,
            });
        } catch (error) {
            if (
                error instanceof UnAuthorizedException ||
                error?.response?.status === 401
            ) {
                setTransactionError(
                    'Användarnamn eller lösenord är felaktigt.',
                    'error'
                );
            } else {
                setTransactionError(
                    'Ett fel inträffade vid signering. Försök igen.',
                    'error'
                );
            }
            throw error; // CHANGED: Throw error instead of returning null
        }
    };

    const saveTransaction = async (signatureObject) => {
        try {
            const res: AxiosResponse = await TransactionService.saveTransaction(
                signatureObject,
                transactionType
            );
            if (res?.status === 204 || res.status === 202) {
                setWarningMessage(
                    'Saldot stämmer inte överens med det som finns i systemet. Klicka i rutan för att godkänna nytt saldo.'
                );
            }
            return res;
        } catch (error) {
            setTransactionError(
                'Något gick fel vid sparandet av transaktionen. Försök igen.',
                'error'
            );
        }
    };

    const doubleSign = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isLoading || hasSucceeded) return;
        clearMessages();

        try {
            setIsLoading(true);
            const formData = new FormData(event.currentTarget);
            const emailOrUserName = formData.get('emailOrUserName') as string;
            const password = formData.get('password') as string;

            const signRes = await signTransaction(emailOrUserName, password);

            if (signRes.userName === profile.userName) {
                setTransactionError(
                    'Det måste vara två olika användare som signerar.',
                    'error'
                );
                return;
            }

            if (signRes?.id !== null) {
                let res: AxiosResponse | undefined;
                if (
                    connectedTransactionRecordIds &&
                    connectedTransactionRecordIds.length > 0
                ) {
                    const inventorySignatureData: InventoryDoubleSignatureData =
                        {
                            signedRecords: connectedTransactionRecordIds.map(
                                (recordId) => ({
                                    userId: signRes.id,
                                    recordId,
                                })
                            ),
                        };
                    res = await TransactionService.saveInventoryDoubleSignature(
                        inventorySignatureData
                    );
                } else {
                    const signatureData: DoubleSignatureData = {
                        userId: signRes.id,
                        recordId: connectedTransactionRecordId,
                    };
                    res =
                        await TransactionService.saveDoubleSignature(
                            signatureData
                        );
                }

                if (res?.status === 200) {
                    resetOverviews();
                    resetTransactionFormData();
                    resetAmbulanceFormData();
                    resetShiftChangeInventoryFormData();
                    resetDisposedFormData();

                    resetSelectedDrug();
                    resetAvailableStorage();
                    resetPatient();
                    resetNote();
                    resetReferensData();
                    resetInventoryRecords();
                    resetInventoryList();
                    resetDisposedCause();
                    resetBarCodeState();

                    navigate(NavigationRoutes.transaction);
                    setMessage('Registreringen har sparats', 'success');
                    setHasSucceeded(true);
                }
            }
        } catch (err) {
            setTransactionError(
                'Användarnamn eller lösenord är felaktigt.',
                'error'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const signAndSaveTransaction = async (
        event: React.FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();
        if (isLoading || hasSucceeded) return;
        clearMessages();

        if (isProfileLoading) {
            setTransactionError(
                'Profilen laddas fortfarande, vänta ett ögonblick.',
                'info'
            );
            return;
        }
        if (!isProfileLoaded) {
            setTransactionError(
                'Misslyckades hämta profil. Försök igen.',
                'error'
            );
            return;
        }

        const formData = new FormData(event.currentTarget);
        const emailOrUserName = formData.get('emailOrUserName') as string;
        const password = formData.get('password') as string;

        try {
            setIsLoading(true);
            const user = await signTransaction(emailOrUserName, password);

            const signatureData = {
                userId: user.id,
                username: user.userName,
                firstname: user.firstName,
                lastname: user.lastName,
            };
            const updatedData = populateData(signatureData);
            const response: AxiosResponse<any, any> | undefined =
                await saveTransaction(updatedData);

            if (response?.status === 200) {
                if (transactionType === TransactionType.AmbulanceTransaction) {
                    const recordIds = response.data.map(
                        (record: any) => record.id
                    );
                    setConnectedTransactionRecordIds(recordIds);
                    const id = response.data[0].id;
                    setConnectedTransactionRecordId(id);
                    setAmbulanceOverview(TransactionOverview.successOverview);
                } else if (transactionType === TransactionType.CheckOut) {
                    setConnectedTransactionRecordId(response.data.id);
                    setWithdralOverview(TransactionOverview.successOverview);
                } else if (transactionType === TransactionType.CheckIn) {
                    setConnectedTransactionRecordId(response.data.id);
                    setDepositOverview(TransactionOverview.successOverview);
                } else if (
                    transactionType === TransactionType.ShiftCrosscheck
                ) {
                    setShiftChangeInventoryOverview(
                        TransactionOverview.successOverview
                    );
                    const recordIds = response.data.map(
                        (record: any) => record.id
                    );
                    setConnectedTransactionRecordIds(recordIds);
                    const id = response.data[0].id;
                    setConnectedTransactionRecordId(id);
                } else if (transactionType === TransactionType.Crosscheck) {
                    setInventoryRecordOverview(
                        TransactionOverview.successOverview
                    );
                    if (Array.isArray(response.data)) {
                        const recordIds = response.data.map((record: any) =>
                            typeof record === 'string' ? record : record.id
                        );
                        setConnectedTransactionRecordIds(recordIds);
                        const id = response.data[0];
                        setConnectedTransactionRecordId(id);
                    } else {
                        const id = response.data;
                        setConnectedTransactionRecordId(id);
                    }
                } else if (transactionType === TransactionType.Disposed) {
                    setDisposedOverview(TransactionOverview.successOverview);
                    const id = response.data.id;
                    setConnectedTransactionRecordId(id);
                    setDisposedOverview(TransactionOverview.successOverview);
                }
                setHasSucceeded(true);
            }
        } catch (error) {
            setTransactionError(
                'Användarnamn eller lösenord är felaktigt.',
                'error'
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="w-full flex justify-center space-x-2">
                <div className="flex w-96">
                    {isTransactionErrorVisible && transactionErrorMessage && (
                        <Box width="100%">
                            <Alert
                                severity={transactionErrorType}
                                sx={{ width: '100%', mb: 2 }}
                            >
                                {transactionErrorMessage}
                            </Alert>
                        </Box>
                    )}
                    <Visibility visible={!!warningMessage}>
                        <Alert
                            severity="warning"
                            sx={{
                                width: '100%',
                                borderRadius: 0,
                                mb: 2,
                                py: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Checkbox
                                checked={acceptSaldoMismatchCheckbox}
                                onChange={() =>
                                    setAcceptSaldoMismatchCheckbox(
                                        !acceptSaldoMismatchCheckbox
                                    )
                                }
                                sx={{ mr: 1 }}
                            />
                            {warningMessage}
                        </Alert>
                    </Visibility>
                </div>
            </div>
            <div className="w-full flex justify-center space-x-2">
                <div className="flex w-96">
                    <form
                        className="space-y-4 md:space-y-6 mt-6"
                        onSubmit={
                            isDoubleSignature
                                ? doubleSign
                                : signAndSaveTransaction
                        }
                    >
                        <IconTextField
                            type="text"
                            required
                            fullWidth
                            id="emailOrUserNameID"
                            label="Användarnamn"
                            name="emailOrUserName"
                            autoComplete="off"
                            value={
                                isDoubleSignature
                                    ? undefined
                                    : profile?.userName || ''
                            }
                            prefix={<PersonOutlineOutlinedIcon />}
                        />
                        <IconTextField
                            required
                            fullWidth
                            id="password"
                            name="password"
                            label="Lösenord"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="off"
                            prefix={
                                <FontAwesomeIcon
                                    icon={faLock}
                                    className="text-gray-400"
                                />
                            }
                            suffix={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            showPassword
                                                ? 'Dölj lösenord'
                                                : 'Visa lösenord'
                                        }
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        onMouseUp={handleMouseUpPassword}
                                        edge="end"
                                    >
                                        <FontAwesomeIcon
                                            icon={
                                                showPassword
                                                    ? faEyeSlash
                                                    : faEye
                                            }
                                            className="text-gray-400"
                                        />
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            startIcon={
                                isProfileLoading || isLoading ? (
                                    <CircularProgress size={24} color="info" />
                                ) : (
                                    <EditIcon />
                                )
                            }
                            sx={{
                                backgroundColor: '#0b4f76',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#083e5a',
                                },
                            }}
                            disabled={
                                isLoading ||
                                hasSucceeded ||
                                isProfileLoading ||
                                isProfileLoaded === false ||
                                editMode ||
                                signButtonDisabled
                            }
                        >
                            {isDoubleSignature
                                ? 'Dubbelsignering'
                                : 'Signera'}{' '}
                        </Button>
                    </form>
                </div>
            </div>
        </>
    );
}
